import { EventType } from '../../types/ViewModel/EventType';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
 
import overrides from '../../config/eventTypeNameOverrides.json';

interface EventTypeNameOverride {
    eventTypeId: number;
    originalName: string;
    newName: string;
    effectiveDate: string; // ISO date string (YYYY-MM-DD)
    comment?: string;
}

const overrideList = overrides as EventTypeNameOverride[];

/**
 * Gets the overridden event type name based on the event date.
 * Returns null if no override applies.
 *
 * @param eventTypeId - The event type ID to check
 * @param eventDate - The date of the event (used to determine if override applies)
 * @returns The overridden name if applicable, null otherwise
 */
export const getOverriddenEventTypeName = (eventTypeId: number, eventDate: Date | null): string | null => {
    const override = overrideList.find((o) => o.eventTypeId === eventTypeId);
    if (!override) return null;

    // If no event date provided, use current date for new events
    const dateToCheck = eventDate ?? new Date();
    const effectiveDate = new Date(override.effectiveDate);

    // Set times to midnight for date-only comparison
    const checkDate = new Date(dateToCheck.getFullYear(), dateToCheck.getMonth(), dateToCheck.getDate());
    const effective = new Date(effectiveDate.getFullYear(), effectiveDate.getMonth(), effectiveDate.getDate());

    // Return the new name if the event date is on or after the effective date
    if (checkDate >= effective) {
        return override.newName;
    }

    return null;
};

/**
 * Resolves the display name for an event type, applying date-based overrides.
 * Used for existing events.
 *
 * @param eventType - The event type to get the display name for
 * @param event - The tankers event (used to get the event date)
 * @returns The resolved display name
 */
export const resolveEventTypeName = (eventType: EventType, event: TankersEvent | null): string => {
    const eventDate = event?.eventDate?.actualDateTimeLocal ?? null;
    return getOverriddenEventTypeName(eventType.eventTypeId, eventDate) ?? eventType.name;
};

/**
 * Resolves the display name for an event type when creating a new event.
 * Uses the current date to determine if override applies.
 *
 * @param eventType - The event type to get the display name for
 * @returns The resolved display name for new events
 */
export const resolveEventTypeNameForNewEvent = (eventType: EventType): string => {
    return getOverriddenEventTypeName(eventType.eventTypeId, new Date()) ?? eventType.name;
};

export type { EventTypeNameOverride };
